import React, { useMemo, useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { checkoutApi } from "../../lib/checkoutApi"
import "./Checkout.scss"

const STEPS = {
  SUMMARY: "summary",
  CUSTOMER: "customer",
  SHIPPING: "shipping",
  REVIEW: "review",
}

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return "$0.00"
  }

  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return `$${value}`
  }

  return `$${numericValue.toFixed(2)}`
}

const Checkout = () => {
  const [cart, setCart] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [step, setStep] = useState(STEPS.SUMMARY)
  const [shippingMethods, setShippingMethods] = useState([])
  const [form, setForm] = useState({
    email: "",
    billingAddress: {
      firstName: "",
      lastName: "",
      address1: "",
      city: "",
      state: "",
      zipCode: "",
      countryCode: "US",
    },
    shippingAddress: {
      firstName: "",
      lastName: "",
      address1: "",
      city: "",
      state: "",
      zipCode: "",
      countryCode: "US",
    },
    useBillingForShipping: true,
    shippingMethodHandle: "",
  })
  const history = useHistory()

  const selectedShippingMethod = useMemo(() => {
    return shippingMethods.find((method) => method.handle === form.shippingMethodHandle)
  }, [shippingMethods, form.shippingMethodHandle])

  useEffect(() => {
    fetchCart()
  }, [])

  const fetchCart = async () => {
    try {
      const cartNumber = localStorage.getItem('cartNumber')
      const data = await checkoutApi.getCart(cartNumber)

      if (data?.cart) {
        setCart(data.cart)
      } else {
        setError("Could not load cart")
      }
    } catch (err) {
      console.error("Error fetching cart:", err)
      setError("Could not load cart")
    } finally {
      setLoading(false)
    }
  }

  const refreshCartFromResponse = (data) => {
    if (!data?.cart) {
      return
    }

    setCart(data.cart)

    if (data.cart.number) {
      localStorage.setItem('cartNumber', data.cart.number)
    }
  }

  const clearErrors = () => {
    setError(null)
    setFieldErrors({})
  }

  const getFieldError = (key) => {
    const value = fieldErrors[key]

    if (Array.isArray(value)) {
      return value[0]
    }

    if (typeof value === "string") {
      return value
    }

    return null
  }

  const updateField = (path, value) => {
    setForm((previousForm) => {
      const nextForm = { ...previousForm }
      const keys = path.split('.')
      let pointer = nextForm

      for (let i = 0; i < keys.length - 1; i += 1) {
        pointer[keys[i]] = { ...pointer[keys[i]] }
        pointer = pointer[keys[i]]
      }

      pointer[keys[keys.length - 1]] = value
      return nextForm
    })
  }

  const handleStartCheckout = async () => {
    try {
      clearErrors()
      setSubmitting(true)

      const cartNumber = localStorage.getItem('cartNumber')
      const data = await checkoutApi.start(cartNumber)
      refreshCartFromResponse(data)
      setShippingMethods(data.shippingMethods || [])

      if (!form.shippingMethodHandle && data.shippingMethods?.length) {
        updateField("shippingMethodHandle", data.shippingMethods[0].handle)
      }

      setStep(STEPS.CUSTOMER)
    } catch (err) {
      setError(err.message || "Could not start checkout")
    } finally {
      setSubmitting(false)
    }
  }

  const handleCustomerSubmit = async () => {
    try {
      clearErrors()
      setSubmitting(true)

      const payload = {
        email: form.email,
        billingAddress: form.billingAddress,
        shippingAddress: form.useBillingForShipping ? form.billingAddress : form.shippingAddress,
      }

      const data = await checkoutApi.customer(payload)
      refreshCartFromResponse(data)

      if (data.shippingMethods) {
        setShippingMethods(data.shippingMethods)
      }

      setStep(STEPS.SHIPPING)
    } catch (err) {
      setError(err.message || "Could not save customer details")
      setFieldErrors(err.errors || {})
    } finally {
      setSubmitting(false)
    }
  }

  const handleShippingSubmit = async () => {
    try {
      clearErrors()
      setSubmitting(true)

      const cartNumber = localStorage.getItem('cartNumber')
      const data = await checkoutApi.shippingMethod(form.shippingMethodHandle, cartNumber)
      refreshCartFromResponse(data)
      setStep(STEPS.REVIEW)
    } catch (err) {
      setError(err.message || "Could not set shipping method")
      setFieldErrors(err.errors || {})
    } finally {
      setSubmitting(false)
    }
  }

  const handleProceedToPayment = () => {
    const cartNumber = localStorage.getItem('cartNumber')
    const cmsBase = process.env.REACT_APP_API?.replace('/api', '') || 'http://lac.local'
    window.location.href = `${cmsBase}/checkout/payment?number=${cartNumber}`
  }

  if (loading) {
    return (
      <div className="checkout-container">
        <div className="checkout-content">
          <p className="center-content">Loading checkout...</p>
        </div>
      </div>
    )
  }

  if (error && !cart) {
    return (
      <div className="checkout-container">
        <div className="checkout-content center">
          <div className="page-header center pad">
            <h1>Checkout Error</h1>
            <p className="margin-top subheader">{error}</p>
          </div>
          <button className="button defaultRed" onClick={() => history.push('/')}>
            Return Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <div className="page-header">
          <h1>Checkout</h1>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {cart && (
          <>
            {cart.itemCount === 0 ? (
              <div className="empty-cart">
                <p>Your cart is empty</p>
                <button className="button defaultRed" onClick={() => history.push('/')}>
                  Back to home
                </button>
              </div>
            ) : (
              <>
                <div className="cart-summary">
                  <h2>Order Summary</h2>
                  <div className="cart-details">
                    {cart.lineItems && cart.lineItems.length > 0 && (
                      <div className="line-items">
                        {cart.lineItems.map((item, index) => (
                          <div key={item.id || index} className="line-item">
                            <div className="item-description">{item.description}</div>
                            <div className="item-details">
                              <span>Qty: {item.qty}</span>
                              <span>{formatPrice(item.subtotal)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="cart-row total">
                      <span>Total:</span>
                      <span>{formatPrice(cart.totalPrice)}</span>
                    </div>
                  </div>
                </div>

                {step === STEPS.SUMMARY && (
                  <div className="checkout-actions">
                    <button
                      className="button secondary"
                      onClick={() => history.push('/')}
                    >
                      Back to Home
                    </button>
                    <button
                      className="button defaultRed"
                      onClick={handleStartCheckout}
                      disabled={submitting}
                    >
                      {submitting ? "Starting..." : "Start Checkout"}
                    </button>
                  </div>
                )}

                {step === STEPS.CUSTOMER && (
                  <div className="checkout-step">
                    <h3>Customer details</h3>
                    <div className="checkout-form-grid">
                      <input
                        type="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={(event) => updateField("email", event.target.value)}
                      />
                      {getFieldError("email") && <p className="field-error">{getFieldError("email")}</p>}

                      <h4>Billing address</h4>
                      <input placeholder="First name" value={form.billingAddress.firstName} onChange={(event) => updateField("billingAddress.firstName", event.target.value)} />
                      <input placeholder="Last name" value={form.billingAddress.lastName} onChange={(event) => updateField("billingAddress.lastName", event.target.value)} />
                      <input placeholder="Address" value={form.billingAddress.address1} onChange={(event) => updateField("billingAddress.address1", event.target.value)} />
                      <input placeholder="City" value={form.billingAddress.city} onChange={(event) => updateField("billingAddress.city", event.target.value)} />
                      <input placeholder="State" value={form.billingAddress.state} onChange={(event) => updateField("billingAddress.state", event.target.value)} />
                      <input placeholder="ZIP" value={form.billingAddress.zipCode} onChange={(event) => updateField("billingAddress.zipCode", event.target.value)} />

                      <label className="checkbox-field">
                        <input
                          type="checkbox"
                          checked={form.useBillingForShipping}
                          onChange={(event) => updateField("useBillingForShipping", event.target.checked)}
                        />
                        Use billing address for shipping
                      </label>

                      {!form.useBillingForShipping && (
                        <>
                          <h4>Shipping address</h4>
                          <input placeholder="First name" value={form.shippingAddress.firstName} onChange={(event) => updateField("shippingAddress.firstName", event.target.value)} />
                          <input placeholder="Last name" value={form.shippingAddress.lastName} onChange={(event) => updateField("shippingAddress.lastName", event.target.value)} />
                          <input placeholder="Address" value={form.shippingAddress.address1} onChange={(event) => updateField("shippingAddress.address1", event.target.value)} />
                          <input placeholder="City" value={form.shippingAddress.city} onChange={(event) => updateField("shippingAddress.city", event.target.value)} />
                          <input placeholder="State" value={form.shippingAddress.state} onChange={(event) => updateField("shippingAddress.state", event.target.value)} />
                          <input placeholder="ZIP" value={form.shippingAddress.zipCode} onChange={(event) => updateField("shippingAddress.zipCode", event.target.value)} />
                        </>
                      )}
                    </div>

                    <div className="checkout-actions">
                      <button className="button secondary" onClick={() => setStep(STEPS.SUMMARY)} disabled={submitting}>
                        Back
                      </button>
                      <button className="button defaultRed" onClick={handleCustomerSubmit} disabled={submitting}>
                        {submitting ? "Saving..." : "Continue to Shipping"}
                      </button>
                    </div>
                  </div>
                )}

                {step === STEPS.SHIPPING && (
                  <div className="checkout-step">
                    <h3>Shipping method</h3>

                    {shippingMethods.length === 0 ? (
                      <p>No shipping methods available.</p>
                    ) : (
                      <div className="method-list">
                        {shippingMethods.map((method) => (
                          <label key={method.handle} className="method-item">
                            <input
                              type="radio"
                              name="shippingMethod"
                              value={method.handle}
                              checked={form.shippingMethodHandle === method.handle}
                              onChange={(event) => updateField("shippingMethodHandle", event.target.value)}
                            />
                            <span>
                              {method.name}
                              {method.price ? ` (${formatPrice(method.price)})` : ""}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="checkout-actions">
                      <button className="button secondary" onClick={() => setStep(STEPS.CUSTOMER)} disabled={submitting}>
                        Back
                      </button>
                      <button
                        className="button defaultRed"
                        onClick={handleShippingSubmit}
                        disabled={submitting || !form.shippingMethodHandle}
                      >
                        {submitting ? "Saving..." : "Review Order"}
                      </button>
                    </div>
                  </div>
                )}

                {step === STEPS.REVIEW && (
                  <div className="checkout-step">
                    <h3>Review order</h3>
                    <div className="review-row">
                      <span>Email</span>
                      <span>{form.email || "-"}</span>
                    </div>
                    <div className="review-row">
                      <span>Shipping</span>
                      <span>{selectedShippingMethod?.name || "-"}</span>
                    </div>

                    <div className="checkout-actions">
                      <button className="button secondary" onClick={() => setStep(STEPS.SHIPPING)} disabled={submitting}>
                        Back
                      </button>
                      <button className="button defaultRed" onClick={handleProceedToPayment} disabled={submitting}>
                        {submitting ? "Proceeding..." : "Proceed to Payment"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Checkout
